import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/react';
import useIsMobile from '../hooks/useIsMobile';

interface SeatUnassignmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUnassign: () => void;
    seatData: {
        seatUuid: string;
        seatNumber: string;
        zone: string;
        row: string;
        seat: string;
    } | null;
    ticketData: {
        ticketId: string;
        ticketName: string;
        price: number;
        description?: string;
    } | null;
}

const SeatUnassignmentModal: React.FC<SeatUnassignmentModalProps> = ({
    isOpen,
    onClose,
    onUnassign,
    seatData,
    ticketData
}) => {
    const isMobile = useIsMobile();

    if (!seatData || !ticketData) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size={isMobile ? 'full' : 'md'}
            scrollBehavior="inside"
            classNames={{
                base: 'bg-white dark:bg-nightElevated rounded-none md:rounded shadow-none md:shadow-xl h-full md:h-auto',
                wrapper: 'p-0 md:p-4 z-[100000]',
            }}
        >
            <ModalContent>
                <ModalHeader className="flex flex-col items-start gap-0.5 border-b border-gray-200 dark:border-nightBorder">
                    <h3 className="text-lg font-semibold text-[#222] dark:text-nightTextSecondary">Unassign Ticket</h3>
                    <p className="text-sm text-gray-600 dark:text-nightMuted font-normal">Remove ticket assignment from this seat</p>
                </ModalHeader>

                {/* Seat Information */}
                <ModalBody className="py-4">
                    <div className="grid grid-cols-3 gap-4 mb-4">
                        {/* Zone */}
                        <div className="text-center">
                            <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">ZONE</div>
                            <div className="text-lg font-bold text-[#222] dark:text-nightTextSecondary">{seatData.zone}</div>
                        </div>
                        {/* Row */}
                        <div className="text-center">
                            <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">ROW</div>
                            <div className="text-lg font-bold text-[#222] dark:text-nightTextSecondary">{seatData.row}</div>
                        </div>
                        {/* Seat */}
                        <div className="text-center">
                            <div className="text-xs text-gray-500 dark:text-nightMuted uppercase tracking-wide mb-1">SEAT</div>
                            <div className="text-lg font-bold text-[#222] dark:text-nightTextSecondary">{seatData.seat}</div>
                        </div>
                    </div>

                    {/* Ticket Information */}
                    <div className="bg-gray-50 dark:bg-nightSurface rounded p-4 mb-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <div className="text-sm text-gray-600 dark:text-nightMuted">Ticket Type</div>
                                <div className="font-semibold text-[#222] dark:text-nightTextSecondary">{ticketData.ticketName}</div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-gray-600 dark:text-nightMuted">Price</div>
                                <div className="font-semibold text-[#222] dark:text-nightTextSecondary">${Number(ticketData.price || 0).toFixed(2)}</div>
                            </div>
                        </div>
                    </div>


                    {/* Warning Message */}
                    <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/40 rounded">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400 dark:text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                                    This will remove the ticket assignment from this seat. The seat will become available for reassignment.
                                </p>
                            </div>
                        </div>
                    </div>
                </ModalBody>

                {/* Action Buttons */}
                <ModalFooter className="p-0 gap-0">
                    <button
                        onClick={onClose}
                        className="flex-1 bg-gray-100 dark:bg-white/10 text-[#222] dark:text-nightTextSecondary font-bold py-4 px-6 hover:bg-gray-200 dark:hover:bg-white/20 transition-colors rounded-bl-none md:rounded-bl"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onUnassign}
                        className="flex-1 bg-red-600 dark:bg-red-500 text-white font-bold py-4 px-6 hover:bg-red-700 dark:hover:bg-red-600 transition-colors rounded-br-none md:rounded-br"
                    >
                        Unassign Ticket
                    </button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SeatUnassignmentModal;
